import type { Obstacle } from './types';

/** Obstacles are solid out to 86% of their drawn radius. */
export const SOLIDITY = 0.86;

/**
 * Push a circle out of a set of circular obstacles. Two passes so that wedges
 * between two overlapping obstacles resolve instead of jittering.
 *
 * The server and the client both call this, which keeps client-side prediction
 * in sync with the authoritative simulation.
 */
export function pushOut(
  x: number,
  y: number,
  radius: number,
  obstacles: readonly Obstacle[],
): { x: number; y: number } {
  let px = x;
  let py = y;
  for (let pass = 0; pass < 2; pass++) {
    for (let i = 0; i < obstacles.length; i++) {
      const o = obstacles[i];
      const dx = px - o.x;
      const dy = py - o.y;
      const min = radius + o.r * SOLIDITY;
      const d2 = dx * dx + dy * dy;
      if (d2 >= min * min) continue;
      const d = Math.sqrt(d2);
      if (d < 0.0001) {
        px = o.x + min;
        continue;
      }
      const push = (min - d) / d;
      px += dx * push;
      py += dy * push;
    }
  }
  return { x: px, y: py };
}

/** Cheap broad-phase filter for callers that have no spatial index. */
export function nearbyObstacles(
  obstacles: readonly Obstacle[],
  x: number,
  y: number,
  radius: number,
): Obstacle[] {
  const out: Obstacle[] = [];
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i];
    const reach = radius + o.r;
    if (Math.abs(o.x - x) > reach || Math.abs(o.y - y) > reach) continue;
    out.push(o);
  }
  return out;
}

/** True when a circle at (x, y) overlaps any obstacle in the list. */
export function blockedAt(
  x: number,
  y: number,
  radius: number,
  obstacles: readonly Obstacle[],
): boolean {
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i];
    const min = radius + o.r * SOLIDITY;
    const dx = x - o.x;
    const dy = y - o.y;
    if (dx * dx + dy * dy < min * min) return true;
  }
  return false;
}
